import axios from "axios";
import BaseApi from "./base-api";
import HTTPError from "./HTTPError";

/**
 * Class for the LightReach API
 *
 * @extends BaseApi
 * @description This class is used to interact with the LightReach endpoint
 */
class LightReachApi extends BaseApi {
  constructor() {
    super("light-reach/light-reach.php");

    this.methods = {
      CREATE_ACCT: "createAcct",
      UPDATE_ACCT: "updateAcct",
      GET_QUOTE: "getQuoteV2",
      GET_ESTIMATED_PRICING: "getEstimatedPricingV2",
      GET_PRICING: "getPricing_v3",
      UPDATE_SYSTEM_DESIGN_INFORMATION: "updateSystemDesignInformation",
      SEND_CONTRACT: "send_contract",
      CHECK_CUSTOMER_STATUS: "checkCustomerStatus",
      EMAIL_CREDIT_APPLICATION: "emailCreditApplication",
      UPLOAD_DOCUMENT: "uploadDocument",
      VERIFY_USER: "verify_user",
    };
  }

  /**
   * Verify the sales rep for LightReach (e.g. to allow opening credit application).
   * @param {string} rep_email - Sales representative email address
   * @returns {Promise<{success: boolean}>}
   */
  verifyUser = async (rep_email) => {
    const response = await this.request({
      method: this.methods.VERIFY_USER,
      rep_email,
    });
    if (response) return response;
    throw new HTTPError(this.status, this.statusText, this.url);
  };

  /**
   * Upload document(s) for a LightReach account.
   * @param {string} acctId - The LightReach account id for the customer
   * @param {string} type - Document type: Disclosure | Supporting | Utility Bill | Voided Check
   * @param {File[]} files - Array of File objects
   * @returns {Promise<object>}
   */
  uploadDocument = async (acctId, type, files) => {
    const formData = new FormData();
    formData.append("method", this.methods.UPLOAD_DOCUMENT);
    formData.append("acctId", acctId);
    formData.append("type", type);
    if (Array.isArray(files)) {
      files.forEach((file) => formData.append("files[]", file));
    }
    const url = this.getUrl();
    const res = await axios.post(url, formData);
    this.status = res.status;
    this.statusText = res.statusText;
    this.url = url;
    if (res.data) return res.data;
    throw new HTTPError(this.status, this.statusText, this.url);
  };

  /**
   * Email the credit application to the customer.
   * @param {string} firstName - Customer first name
   * @param {string} acctId - The LightReach account id for the customer
   * @param {string} email - Customer email address
   */
  emailCreditApplication = async (firstName, acctId, email) => {
    const response = await this.request({
      method: this.methods.EMAIL_CREDIT_APPLICATION,
      firstName,
      acctId,
      email,
    });
    if (response) {
      return response;
    }
    throw new HTTPError(this.status, this.statusText, this.url);
  };

  /**
   * Check the customer status for a LightReach account.
   * @param {string} acctId - The LightReach account id for the customer
   * @returns {Promise<{status?: string}>} Response includes status
   */
  checkCustomerStatus = async (acctId) => {
    const response = await this.request({
      method: this.methods.CHECK_CUSTOMER_STATUS,
      acctId,
    });
    if (response) {
      return response;
    }
    throw new HTTPError(this.status, this.statusText, this.url);
  };

  /**
   * Send or resend the contract for a LightReach account.
   * @param {string} accountId - The LightReach account id for the customer
   * @param {string} externalId - The customer's Salesforce project id (e.g. from URL param proj_id)
   */
  sendContract = async (accountId, externalId) => {
    const response = await this.request({
      method: this.methods.SEND_CONTRACT,
      account_id: accountId,
      external_id: externalId,
    });
    if (response) {
      return response;
    }
    throw new HTTPError(this.status, this.statusText, this.url);
  };

  /**
   * Get a list of all quotes for a given account.
   * When acctId is omitted, the API creates a LightReach account, returns pricing, and returns the new acctId.
   */
  getQuote = async ({
    acctId,
    sfProjectId,
    hasBattery,
    msp,
    batteryCost,
    batteryCount,
  }) => {
    const response = await this.request({
      method: this.methods.GET_QUOTE,
      acctId,
      sfProjectId,
      hasBattery,
      msp,
      batteryCost,
      batteryCount,
    });
    if (response) {
      return response;
    }

    throw new HTTPError(this.status, this.statusText, this.url);
  };

  getPricing = async (payload) => {
    const response = await this.request({
      method: this.methods.GET_PRICING,
      acctId: payload.acctId,
      prodId: payload.productId,
      msp: payload.msp,
      batteryCost: payload.batteryCost,
      batteryCount: payload.batteryCount,
    });

    if (response) return response;

    throw new HTTPError(this.status, this.statusText, this.url);
  };

  /**
   * Update the system design information for a given account
   */
  updateSystemDesignInformation = async (payload) => {
    const response = await this.request({
      method: this.methods.UPDATE_SYSTEM_DESIGN_INFORMATION,
      ...payload,
    });

    if (response) return response;

    throw new HTTPError(this.status, this.statusText, this.url);
  };

  /**
   * Get a list of pricing for a given set of parameters
   *
   * @param {object} data
   * @param {string} data.productId
   * @param {string} data.utilityLseId
   * @param {string} data.state
   * @param {number} data.systemSize
   * @param {number} data.production
   * @param {boolean} data.msp
   * @param {boolean} data.battery
   * @param {number} data.batteryAmount
   *
   */
  getEstimatedPricing = async (data) => {
    const response = await this.request({
      method: this.methods.GET_ESTIMATED_PRICING,
      details: JSON.stringify({
        accountId: data.accountId,
        financeProductId: data.productId,
        programType: "solar",
        lseId: data.utilityLseId,
        state: data.state,
        systemSizeKw: data.systemSize,
        systemFirstYearProductionKwh: data.production,
        backupBatteryIncluded: data.battery,
        electricalUpgradeIncluded: data.msp,
        backupBatteryCost: data.batteryAmount,
      }),
    });

    if (response) {
      return response;
    }

    throw new Error("Could not get LightReach Quote");
  };
}

export default new LightReachApi();
